import React, { useEffect, useState, useCallback } from 'react';
import {
    Paper, Typography, CircularProgress, Grid, Box, Button, Chip,
    useTheme, useMediaQuery,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import BloodtypeOutlinedIcon from '@mui/icons-material/BloodtypeOutlined';
import CoronavirusOutlinedIcon from '@mui/icons-material/CoronavirusOutlined';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import apiService from '../../services/actions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import { ClickAwayListener } from '@mui/material';
const T = {
    primary: '#1976d2',
    primaryLight: '#e3f2fd',
    primaryMid: '#bbdefb',
    primaryDark: '#1565c0',
    secondary: '#9c27b0',
    border: '#e6eaf0',
    textPrimary: '#1a2027',
    textSecondary: '#6b7280',
    pageBg: '#eef1f6',
    cardShadow: '0 2px 10px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04)',
    cardShadowHover: '0 6px 18px rgba(16, 24, 40, 0.09)',
    lineColorDone: '#90caf9',
    success: '#16a34a',
    successLight: '#dcfce7',
};

// 1) Registry of every icon this screen knows how to render, keyed by a
//    plain string. If the backend sends an `icon` key that matches one of
//    these, it wins — no redeploy needed to point a new test at an icon.
const ICON_REGISTRY = {
    blood: WaterDropOutlinedIcon,
    droplet: WaterDropOutlinedIcon,
    vials: ScienceOutlinedIcon,
    flask: BiotechOutlinedIcon,
    liver: EditNoteOutlinedIcon,
    heart: MonitorHeartOutlinedIcon,
    thyroid: MonitorHeartOutlinedIcon,
    sun: WbSunnyOutlinedIcon,
    sugar: BloodtypeOutlinedIcon,
    infection: CoronavirusOutlinedIcon,
    medication: MedicationOutlinedIcon,
    cardio: FavoriteBorderOutlinedIcon,
    default: ScienceOutlinedIcon,
};

// 2) Fallback for known internal test codes, in case the API doesn't send
//    an icon key at all.
const TEST_CODE_ICON_MAP = {
    CBC: 'blood',
    LIPID: 'vials',
    LFT: 'liver',
    THYROID: 'thyroid',
    VITD: 'sun',
    FBS: 'sugar',
    URINE: 'flask',
};

// 3) Last resort: guess from the test name itself, so a totally new test
//    the backend has never told us about still gets a sensible icon
//    instead of always falling back to the generic default.
const KEYWORD_ICON_RULES = [
    { keywords: ['blood count', 'cbc', 'hemoglobin', 'hb '], icon: 'blood' },
    { keywords: ['lipid', 'cholesterol'], icon: 'vials' },
    { keywords: ['liver', 'lft', 'sgpt', 'sgot'], icon: 'liver' },
    { keywords: ['thyroid', 'tsh', 't3', 't4'], icon: 'thyroid' },
    { keywords: ['vitamin d', 'vitamin b', 'vitamin'], icon: 'sun' },
    { keywords: ['sugar', 'glucose', 'diabet', 'hba1c'], icon: 'sugar' },
    { keywords: ['urine', 'urinalysis'], icon: 'flask' },
    { keywords: ['covid', 'infection', 'culture', 'fever'], icon: 'infection' },
    { keywords: ['cardio', 'heart', 'ecg', 'lipoprotein'], icon: 'cardio' },
];

/**
 * Resolves which icon component to render for a booked test.
 * Priority: explicit `icon` key from API > known testCode > keyword
 * match on testName > generic default.
 */
function resolveTestIconKey(test) {
    if (test?.icon && ICON_REGISTRY[test.icon]) {
        return test.icon;
    }
    if (test?.testCode && TEST_CODE_ICON_MAP[test.testCode]) {
        return TEST_CODE_ICON_MAP[test.testCode];
    }
    const haystack = [test?.testName, test?.category, test?.sampleType]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    const match = KEYWORD_ICON_RULES.find(rule =>
        rule.keywords.some(kw => haystack.includes(kw))
    );
    if (match) return match.icon;
    // sampleType is a decent generic hint even with no name match
    if (test?.sampleType?.toLowerCase() === 'blood') return 'blood';
    if (test?.sampleType?.toLowerCase() === 'urine') return 'flask';
    return 'default';
}

// Raw shape exactly as returned by apiService.getTracking, used as a
// fallback so the screen still renders during development/outages.
const MOCK_TRACKING_DATA = {
    orderId: '1785240495585',
    patientName: 'PRAVEEN  SAHU',
    patientId: 'PPBwX9296p',
    age: 25,
    gender: 'M',
    orderStatus: 'Completed',
    // External lab's own confirmation/reference number for this order —
    // only present when the sample was routed to a third-party/external
    // lab for processing. Absent for in-house orders.
    extLabConfirmId: 'EXT-CNF-88213',
    tracking: [
        { orderId: '1785240495585', consignmentId: null, sampleStatusUpdated: '2026-07-28T17:38:35', sampleStatus: 'Order Placed', sampleStatusId: 19 },
        { orderId: '1785240495585', consignmentId: null, sampleStatusUpdated: '2026-07-28T21:59:21', sampleStatus: 'Bar Code Mapped', sampleStatusId: 14 },
        { orderId: '1785240495585', consignmentId: null, sampleStatusUpdated: '2026-07-28T21:59:37', sampleStatus: 'Sample Collected', sampleStatusId: 1 },
        { orderId: '1785240495585', consignmentId: null, sampleStatusUpdated: '2026-07-28T22:00:37', sampleStatus: 'Centrifuge Mapped', sampleStatusId: 15 },
        { orderId: '1785240495585', consignmentId: null, sampleStatusUpdated: '2026-07-28T21:59:54', sampleStatus: 'Sample ready to dispatch', sampleStatusId: 2 },
        { orderId: '1785240495585', consignmentId: null, sampleStatusUpdated: '2026-07-29T10:30:00', sampleStatus: 'Report Uploaded', sampleStatusId: 22 },
    ],
    testsBooked: {
        '1785240495585': [
            {
                id: 75927, patientid: 'PPBwX9296p', patientlabtestorderid: '1785240495585',
                labtestcode: '4448496', labtestname: 'CBC (Complete Blood Count)',
                labtesttype: 'Pathology', tat: 'Same Day', sampleType: 'BLOOD',
                sampleColor: 'Lavender (EDTA)', category: 'OTHERS',
                preTestInfo: 'None required; patient may eat and drink normally.',
            },
        ],
    },
};

// The public tracking screen always shows exactly these milestones, in
// this order — regardless of how many intermediate statuses (Bar Code
// Mapped, Centrifuge Mapped, etc.) the backend actually sends. Each one is
// marked completed only if a matching event is present in the response;
// otherwise it renders as the existing "pending" (unfilled) step style.
//
// matchIds: sampleStatusId values from the backend's workflow engine that
// count as satisfying this milestone.
// matchText: lowercase keywords checked against `sampleStatus` as a
// fallback, in case an environment sends a different id for the same
// logical step.
// optional: if true, this step is left out of the rendered timeline
// entirely when no matching event is present (instead of showing as a
// permanently "pending" placeholder) — for steps that don't apply to
// every order, e.g. a phlebotomist visit for home collection.
const CANONICAL_TRACKING_STEPS = [
    { label: 'Order Placed', matchIds: [19], matchText: ['order placed'] },
    { label: 'Phlebo On the Way', matchIds: [101], matchText: ['phlebo on the way', 'phlebo'], optional: true },
    { label: 'Sample Drawn', matchIds: [1], matchText: ['sample drawn', 'sample collected'] },
    { label: 'Received at eClinic', matchIds: [12], matchText: ['received at clinic', 'received at eclinic', 'sample received'] },
    { label: 'Reached Lab', matchIds: [5], matchText: ['reached lab', 'reached laboratory'] },
    { label: 'Report Uploaded', matchIds: [21, 22], matchText: ['report uploaded', 'report generated'] },
];

function buildCanonicalTracking(rawTrackingArr) {
    const events = rawTrackingArr || [];

    return CANONICAL_TRACKING_STEPS
        .map((canonicalStep) => {
            const matches = events.filter((item) => {
                if (canonicalStep.matchIds.includes(item.sampleStatusId)) return true;
                const text = (item.sampleStatus || '').toLowerCase();
                return canonicalStep.matchText.some((kw) => text.includes(kw));
            });

            if (matches.length === 0) {
                if (canonicalStep.optional) return null;
                return { step: canonicalStep.label, status: 'pending', date: null, time: null };
            }

            // if more than one raw event maps to this milestone, use the most
            // recent timestamp among them
            const latest = matches.reduce((a, b) =>
                new Date(a.sampleStatusUpdated) > new Date(b.sampleStatusUpdated) ? a : b
            );

            return {
                step: canonicalStep.label,
                status: 'completed',
                date: formatDate(latest.sampleStatusUpdated),
                time: formatTime(latest.sampleStatusUpdated),
            };
        })
        .filter(Boolean);
}

function formatDate(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

const GENDER_MAP = { M: 'Male', F: 'Female', O: 'Other' };

/**
 * Adapts the raw API response into the shape the UI components expect.
 * Keeping this in one place means if the backend renames a field, only
 * this function needs to change — not every component below.
 */
function normalizeTrackingResponse(raw) {
    if (!raw) return null;

    const tracking = buildCanonicalTracking(raw.tracking);

    // testsBooked is an object keyed by orderId -> array of full test-line
    // records. Flatten it (handles either shape defensively).
    let testLines = [];
    if (Array.isArray(raw.testsBooked)) {
        testLines = raw.testsBooked;
    } else if (raw.testsBooked && typeof raw.testsBooked === 'object') {
        testLines = Object.values(raw.testsBooked).flat();
    }

    const testsBooked = testLines.map((t) => ({
        testCode: t.labtestcode || t.testCode,
        testName: t.labtestname || t.testName,
        category: t.category,
        sampleType: t.sampleType,
        tat: t.tat,
        icon: t.icon, // honored if backend ever sends one directly
    }));

    return {
        orderId: raw.orderId,
        patientName: (raw.patientName || '').replace(/\s+/g, ' ').trim(),
        patientId: raw.patientId,
        age: raw.age,
        gender: GENDER_MAP[raw.gender?.toUpperCase()] || raw.gender,
        orderStatus: raw.orderStatus || raw.orderStatus,
        // External lab's own confirmation/reference number for this
        // order. Only present when the sample was routed to a third-party
        // lab, so it's rendered conditionally in the header.
        extLabConfirmId: raw.extLabConfirmId,
        // "NO" = sample was collected at the eClinic itself, not at the
        // patient's home. Only in that case do eclinicName/eclinicAddress
        // apply — for home collection there's no clinic to point to.
        homeSampleCollection: (raw.homeSampleCollection || '').toUpperCase() === 'YES',
        eclinicName: raw.eclinicName,
        eclinicAddress: raw.eclinicAddress,
        tracking,
        testsBooked,
    };
}

// Icon is fully unmounted on mobile (not just visually hidden) so every
// field — whether or not it normally has an icon — starts its label/value
// text at the exact same left edge. From `sm` up, the icon reappears.
// Fields that should never show an icon (e.g. the Order ID / Bill ID
// top section) simply omit the `icon` prop — HeaderField already renders
// icon-free in that case, on every breakpoint.
//
// `tooltip`: opt-in. When true and the value's string length is over 10
// characters, the value is wrapped in an MUI Tooltip so the full string
// is available on hover — useful for fields like Bill ID that render
// with `noWrap` (no `wrap` prop) and can visually truncate.
function HeaderField({ label, value, valueColor, icon: Icon, wrap, tooltip }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showIcon = Icon && !isMobile;

    // Only truncate + tooltip on mobile, where space is tight. On web
    // there's room to just show the full value inline.
    const showTooltip = tooltip && isMobile && value != null && String(value).length > 10;

    const [tooltipOpen, setTooltipOpen] = useState(false);

    const valueEl = (
        <Typography
            sx={{ fontSize: '15px', fontWeight: 700, color: valueColor || T.textPrimary, lineHeight: 1.3, textAlign: 'left' }}
            noWrap={!wrap}
            onClick={showTooltip ? () => setTooltipOpen((prev) => !prev) : undefined}
        >
            {value || '-'}
        </Typography>
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: showIcon ? 1.25 : 0, textAlign: 'left' }}>
            {showIcon && (
                <Box sx={{
                    width: 34, height: 34, borderRadius: '9px', flexShrink: 0, mt: 0.25,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: T.primaryLight,
                }}>
                    <Icon sx={{ fontSize: 17, color: T.primary }} />
                </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: T.textSecondary, mb: 0.4, textAlign: 'left' }}>
                    {label}
                </Typography>
                {showTooltip ? (
                    <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                        <Tooltip
                            title={value}
                            arrow
                            placement="top"
                            open={tooltipOpen}
                            onClose={() => setTooltipOpen(false)}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                        >
                            {valueEl}
                        </Tooltip>
                    </ClickAwayListener>
                ) : (
                    valueEl
                )}
            </Box>
        </Box>
    );
}

// Download Report Button Component
function DownloadReportButton({ orderId, onDownloadReport, isCompleted, downloading }) {
    if (!isCompleted) return null;

    return (
        <Button
            variant="contained"
            size="small"
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlinedIcon />}
            onClick={() => onDownloadReport(orderId)}
            disabled={downloading}
            sx={{
                textTransform: 'none',
                bgcolor: T.success,
                '&:hover': { bgcolor: '#15803d' },
                '&:disabled': { bgcolor: '#86efac' },
                borderRadius: '20px',
                px: 2,
                py: 0.5,
                fontSize: '11px',
                fontWeight: 600,
                minHeight: '28px',
                color: 'white',
            }}
        >
            {downloading ? 'Downloading...' : 'Download Report'}
        </Button>
    );
}

// Redesigned: the whole timeline is wrapped in a centered, width-capped
// container (see the "Tracking Status" Paper below), so on wide desktop
// cards the stepper sits centered instead of hugging the left edge with
// a big empty gap to the right. Within each step, the label and its
// date/time keep a light, deliberate gap between them (not squeezed
// together, not stretched apart).
//
// The "Download Report" action used to live inline on the "Report
// Uploaded" step — it's now rendered once, next to the completed-count
// chip in the section header above the timeline, so this component no
// longer needs orderId/onDownloadReport/downloadingReport.
function TrackingStep({ item, isLast }) {
    const isCompleted = item.status === 'completed';

    return (
        <Box sx={{ display: 'flex', gap: 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: isCompleted ? T.primary : '#fff',
                    border: `2px solid ${isCompleted ? T.primary : '#d7dce3'}`,
                    boxShadow: isCompleted ? `0 0 0 4px ${T.primaryLight}` : 'none',
                    transition: 'all 0.2s ease',
                }}>
                    {isCompleted && <CheckIcon sx={{ fontSize: 17, color: '#fff' }} />}
                </Box>
                {!isLast && (
                    <Box sx={{
                        width: '2px', flexGrow: 1, minHeight: 40, my: 0.5, borderRadius: '2px',
                        bgcolor: isCompleted ? T.lineColorDone : '#e0e4ea',
                    }} />
                )}
            </Box>

            {/* On mobile the label sits directly above its date/time, both
                left-aligned as one tight block next to the marker. From
                `sm` up it's a single row: label on the left, date/time
                pushed to the right edge. A hairline divider runs under
                each row's content (not under the marker column, which
                already has the connecting line) to separate steps, like
                the reference layout. */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 0.75, sm: 1 },
                flexWrap: 'wrap',
                pb: !isLast ? 2.25 : 0.5,
                mb: !isLast ? 1 : 0,
                pt: 0.25,
                borderBottom: !isLast ? `1px solid ${T.border}` : 'none',
            }}>
                <Typography sx={{
                    fontSize: '14.5px', fontWeight: 600,
                    color: isCompleted ? T.textPrimary : T.textSecondary,
                }}>
                    {item.step}
                </Typography>

                {(item.date || item.time) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {item.date && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: T.primary }} />
                                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: T.textPrimary }}>
                                    {formatDate(item.date)}
                                </Typography>
                            </Box>
                        )}
                        {item.time && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: T.primary }} />
                                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: T.textPrimary }}>
                                    {item.time}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function TestChip({ test }) {
    const iconKey = resolveTestIconKey(test);
    const Icon = ICON_REGISTRY[iconKey] || ICON_REGISTRY.default;
    return (
        <Paper
            elevation={0}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.5, borderRadius: '10px', border: `1px solid ${T.border}`,
                height: '100%', transition: 'all 0.15s ease', cursor: 'default',
                '&:hover': { boxShadow: T.cardShadowHover, borderColor: T.primaryMid, transform: 'translateY(-1px)' },
            }}
        >
            <Box sx={{
                width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `linear-gradient(135deg, ${T.primaryLight} 0%, #dbeafe 100%)`,
            }}>
                <Icon sx={{ fontSize: 17, color: T.primary }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: T.textPrimary }}>
                    {test.testName}
                </Typography>
                {(test.sampleType || test.tat) && (
                    <Typography sx={{ fontSize: '11px', color: T.textSecondary, mt: 0.25 }}>
                        {[test.sampleType, test.tat].filter(Boolean).join(' · ')}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
}

function getOrderIdFromUrl() {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('orderId');
}

function SupportFooter() {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            py: 2.5, mt: 0.5,
        }}>
            <Box sx={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: T.primaryLight,
            }}>
                <PhoneInTalkOutlinedIcon sx={{ fontSize: 15, color: T.primary }} />
            </Box>
            <Typography sx={{ fontSize: '13px', color: T.textSecondary, textAlign: 'center' }}>
                Need assistance? Reach us at:{' '}
                <Typography
                    component="a"
                    href="tel:8335000999"
                    sx={{ fontWeight: 700, color: T.primary, textDecoration: 'none' }}
                >
                    +91 8335000999
                </Typography>
            </Typography>
        </Box>
    );
}

function OrderTracking({ orderId: orderIdProp }) {
    // Prop wins if explicitly passed (e.g. rendered from a parent that
    // already knows the order), otherwise fall back to the ?orderId=
    // query param from the current URL, e.g.
    // https://lab-stage.curebay.in/orderPatientTracking?orderId=1785240495585
    const [orderId] = useState(() => orderIdProp || getOrderIdFromUrl());

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [downloadingReport, setDownloadingReport] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const closeSnackbar = useCallback(() => {
        setSnackbar({ open: false, message: '', severity: 'success' });
    }, []);

    const fetchTracking = useCallback(async (id) => {
        if (!id) {
            setError('No order ID found in the URL. Expected a link like ?orderId=1785240495585.');
            return;
        }
        setError(null);
        setLoading(true);
        try {
            const res = await apiService.getTracking(orderId);
            const payload = res?.data?.data;
            setData(normalizeTrackingResponse(payload));
        } catch {
            // Use mock data as fallback
            setData(normalizeTrackingResponse(MOCK_TRACKING_DATA));
        }
        setLoading(false);
    }, [orderId]);

    // Handle report download - uses the same API as bulk download
    const handleDownloadReport = useCallback(async (orderId) => {
        if (!orderId) {
            showSnackbar('Order ID is required', 'error');
            return;
        }

        setDownloadingReport(true);
        try {
            // Use the same API endpoint as bulk download but for a single order
            const response = await apiService.reportBulkDownload({ orderIds: orderId });

            if (!response) {
                throw new Error('No data received from server');
            }

            // Check if response is valid
            if (response.size === 0) {
                throw new Error('No report data available for this order');
            }

            // Create download link
            const url = window.URL.createObjectURL(
                new Blob([response], { type: 'application/zip' })
            );
            const link = document.createElement('a');
            link.href = url;
            link.download = `report_${orderId}_${Date.now()}.zip`;
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Clean up
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);

            showSnackbar('Report downloaded successfully', 'success');
        } catch (error) {
            console.error('Report download failed:', error);
            const errorMessage = error.response?.data?.message ||
                error.message ||
                'Failed to download report. Please try again.';
            showSnackbar(errorMessage, 'error');
        } finally {
            setDownloadingReport(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchTracking(orderId);
    }, [orderId, fetchTracking]);

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', bgcolor: T.pageBg, px: 2 }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: '14px', border: `1px solid ${T.border}`, boxShadow: T.cardShadow, textAlign: 'center', maxWidth: 420 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '15px', color: T.textPrimary, mb: 1 }}>
                        Order not found
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: T.textSecondary }}>
                        {error}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    if (loading || !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', bgcolor: T.pageBg }}>
                <CircularProgress sx={{ color: T.primary }} />
            </Box>
        );
    }

    // Check if order is completed
    const isOrderCompleted = data.orderStatus === 'Completed' || data.orderStatus === 'Complete';

    // Progress summary for the tracking-card header (fills the space that
    // used to sit empty on the right of "TRACKING STATUS").
    const trackingSteps = data.tracking || [];
    const completedStepsCount = trackingSteps.filter((t) => t.status === 'completed').length;

    // Whether the report is ready — drives the "Download Report" button
    // next to the completed-count chip in the Tracking Status header.
    const isReportUploaded = trackingSteps.some(
        (t) => t.step === 'Report Uploaded' && t.status === 'completed'
    );

    // Whether Bill ID / Lab Confirm ID is present — only shown alongside
    // Order ID in the top section when it exists (a blank/whitespace-only
    // string from the API doesn't count as "present").
    const hasExtLabConfirmId = Boolean(data.extLabConfirmId && String(data.extLabConfirmId).trim());

    // Whether the eClinic name/address block has anything to show at all,
    // used to decide the column split for the lower info grid.
    const hasEclinicInfo = !data.homeSampleCollection && (data.eclinicName || data.eclinicAddress);

    return (
        <Box sx={{ bgcolor: T.pageBg, minHeight: '100vh', py: { xs: 2.5, md: 4 }, px: { xs: 2, sm: 3, md: 5 } }}>
            <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                {/* Page title */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: '14px',
                        bgcolor: '#eef1f6',
                        border: `1px solid ${T.border}`,
                    }}
                >
                    <Box sx={{
                        width: 38, height: 38, borderRadius: '10px',
                        background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 3px 10px ${T.primaryMid}`,
                    }}>
                        <LocalShippingOutlinedIcon sx={{ color: '#fff', fontSize: 19 }} />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "18px",
                                color: T.textPrimary,
                                lineHeight: 1.2,
                                mb: 0.2,
                            }}
                        >
                            Order Tracking
                        </Typography>


                    </Box>

                    {/* Order Status Badge */}
                    {isOrderCompleted && (
                        <Box sx={{ ml: 'auto' }}>
                            <Chip
                                label="Completed"
                                sx={{
                                    bgcolor: T.success,
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    height: 36,
                                    px: 1,
                                    borderRadius: '18px',
                                    '& .MuiChip-label': { color: 'white', px: 1.5 },
                                }}
                            />
                        </Box>
                    )}
                </Box>
                {/* Header: Order + Patient info */}
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        borderRadius: '14px',
                        border: `1px solid ${T.border}`,
                        boxShadow: T.cardShadow
                    }}
                >

                    <Grid
                        container
                        spacing={{ xs: 2.5, sm: 3 }}
                        sx={{ pb: 2.5, mb: 2.5, borderBottom: `1px solid ${T.border}` }}
                    >
                        <Grid item xs={6} sm={3}>
                            <HeaderField
                                label="Order ID"
                                value={data.orderId}
                                valueColor={T.primary}
                            />
                        </Grid>
                        {hasExtLabConfirmId && (
                            <Grid item xs={6} sm={9}>
                                <HeaderField
                                    label="Bill ID"
                                    value={data.extLabConfirmId}
                                    tooltip
                                />
                            </Grid>
                        )}
                    </Grid>

                    {/* Patient + sample details — a tidy 12-column grid so
                        columns line up cleanly on web instead of leaving
                        stray gaps: Patient Name / Patient ID each take a
                        wider column, Age / Gender are narrow, and eClinic
                        Name + Sample Collected At share the final row. */}
                    <Grid container spacing={{ xs: 2.5, sm: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <HeaderField label="Patient Name" value={data.patientName} wrap />
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <HeaderField label="Patient ID" value={data.patientId} />
                        </Grid>

                        <Grid item xs={6} sm={2}>
                            <HeaderField label="Age" value={data.age} />
                        </Grid>

                        <Grid item xs={6} sm={2}>
                            <HeaderField label="Gender" value={data.gender} />
                        </Grid>

                        {!data.homeSampleCollection && data.eclinicName && (
                            <Grid item xs={12} sm={2}>
                                <HeaderField
                                    label="eClinic Name"
                                    value={data.eclinicName}
                                />
                            </Grid>
                        )}

                        {!data.homeSampleCollection && data.eclinicAddress && (
                            <Grid item xs={12} sm={hasEclinicInfo && data.eclinicName ? 12 : 12}>
                                <HeaderField
                                    label="Sample Collected At"
                                    value={data.eclinicAddress}
                                    wrap
                                />
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                {/* Tracking timeline */}
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 3.5 }, borderRadius: '14px', border: `1px solid ${T.border}`, boxShadow: T.cardShadow }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: T.textSecondary }}>
                            Tracking Status
                        </Typography>

                        {/* Completed-count chip + Download Report action live together here now */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                label={`${completedStepsCount} of ${trackingSteps.length} completed`}
                                size="small"
                                sx={{
                                    bgcolor: T.primaryLight,
                                    color: T.primaryDark,
                                    fontWeight: 600,
                                    fontSize: '11.5px',
                                    '& .MuiChip-label': { px: 1.25 },
                                }}
                            />
                            {isReportUploaded && (
                                <DownloadReportButton
                                    orderId={data.orderId}
                                    onDownloadReport={handleDownloadReport}
                                    isCompleted={isReportUploaded}
                                    downloading={downloadingReport}
                                />
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ maxWidth: 640, mx: 'auto' }}>
                        {trackingSteps.map((item, idx) => (
                            <TrackingStep
                                key={idx}
                                item={item}
                                isLast={idx === trackingSteps.length - 1}
                            />
                        ))}
                    </Box>
                </Paper>

                {/* Tests / packages booked */}
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: '14px', border: `1px solid ${T.border}`, boxShadow: T.cardShadow }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: T.primary, mb: 2, textAlign: 'left' }}>
                        Order Details (Tests/Packages Booked)
                    </Typography>
                    <Grid container spacing={1.75}>
                        {(data.testsBooked || []).map((test, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={test.testCode || idx}>
                                <TestChip test={test} />
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Support / help footer */}
                <SupportFooter />
            </Box>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ zIndex: 99999 }}
            >
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ zIndex: 99999 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default OrderTracking;